// The active workspace — one client, global to the whole app.
//
// Lifted from competitive-monitoring/ClientContext.jsx, which solved this for one module. The
// value shape is deliberately unchanged from that version ({ clients, clientId, setClientId,
// loading, err }) so the fifteen files consuming it keep working through a re-export shim while
// the rest of the platform catches up.
//
// Mounted inside AuthProvider (it needs the token) and outside the router (the selection has to
// survive navigation).
//
// MODULE-AWARE since Client Management: a client can be enabled for the Taxonomy Generator and
// not for Competitive Monitoring, so the list depends on which module is open. That makes two
// notions of "the workspace" necessary — see storage.js.

import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { createApiClient } from '../http/createApiClient.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { moduleForPath } from '../modules/registry.js';
import { readStoredClientId, writeStoredClientId, writeEffectiveClientId } from './storage.js';

const api = createApiClient({ auth: true });
const WorkspaceContext = createContext(null);

export function WorkspaceProvider({ children }) {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [preferredId, setPreferredId] = useState(readStoredClientId);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  // This provider sits outside the Router and so never re-renders on navigation. It needs its
  // own popstate listener, which works because every in-app navigation already dispatches a
  // synthetic PopStateEvent (HubPage, OrbitModuleHeader.goToHub, TaxonomyApp.navigate,
  // AuthContext.logout) — App.jsx's own router depends on exactly that.
  const [path, setPath] = useState(() => window.location.pathname);
  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Only client-scoped modules narrow the list. On the Hub this is null, which is right: the
  // Hub is not inside a module and should see everything the user holds.
  const moduleId = useMemo(() => {
    const m = moduleForPath(path);
    return m?.clientScoped ? m.id : null;
  }, [path]);

  useEffect(() => {
    // No token yet means the login page: asking for a workspace there would 401 on every
    // render and put noise in the console before anyone has signed in.
    if (!user) { setClients([]); setTotal(0); return; }

    let alive = true;
    setLoading(true); setErr(null);
    api.get('/portal/clients', { params: moduleId ? { module: moduleId } : undefined })
      .then(d => {
        if (!alive) return;
        setClients(d.clients || []);
        setTotal(d.total ?? (d.clients || []).length);
      })
      .catch(e => { if (alive) setErr(e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [user, moduleId]);

  // The effective workspace: the user's pick when this module allows it, else the first client
  // this module does allow. The fallback is transient by design — it is NOT written back to the
  // preferred key, so leaving a module and returning restores the original choice. Persisting
  // it (as this did before the list became per-module) meant merely visiting a tool where your
  // client was disabled silently changed your workspace in the tool you came from.
  const clientId = useMemo(() => {
    if (!clients.length) return null;
    const stillValid = preferredId && clients.some(c => String(c.id) === String(preferredId));
    return stillValid ? String(preferredId) : String(clients[0].id);
  }, [clients, preferredId]);

  // Written on every change because createApiClient reads it at request time, per request.
  useEffect(() => { writeEffectiveClientId(clientId); }, [clientId]);

  const setClientId = useCallback((id) => {
    const next = id ? String(id) : null;
    setPreferredId(next);
    writeStoredClientId(next);   // an explicit pick, and the only thing that updates it
  }, []);

  // String comparison throughout: tx_clients.id is a UUID and round-trips through
  // localStorage as text, so === against a raw value is not reliable.
  const client = useMemo(
    () => clients.find(c => String(c.id) === String(clientId)) || null,
    [clients, clientId],
  );

  const value = useMemo(
    () => ({ clients, clientId, client, setClientId, loading, err, total, moduleId }),
    [clients, clientId, client, setClientId, loading, err, total, moduleId],
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace() {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error('useWorkspace must be used within a WorkspaceProvider');
  return ctx;
}
